// Modules
import type {GetStaticProps} from 'next';
import {useTranslation} from '@app/hooks/use-translation';
import {serverSideTranslations} from '@app/modules/locale-management/server';
import {LandingSections} from '@tempo/landing';
import {TempoLandingPage} from '@app/components/layout/LandingPage';
import type {LandingProps} from '@app/types/next';
import {
	FAQ,
	LyricsClick,
	LyricsHero,
	LyricsTranslated,
	LyricsAcross,
	LyricsShare,
	LyricsSearch,
} from '@app/views/features/lyrics';
import {PlaylistSection} from '@app/views/generic';
import {useAsRailItems} from '@app/modules/ssg-data/utils';
import {useLyricsPageData} from '@app/modules/ssg-data/features/lyrics/client';
import {getFooterLinksPerLang} from '@app/modules/ssg-data/unlogged-data';
import {getLyricsPageGQLData} from '@app/modules/ssg-data/features/lyrics/server';

const LyricsPage: React.FC<LandingProps> = () => {
	const {t} = useTranslation({ns: ['lyrics']});
	const pageTitle = t('lyricslandingpage_text_seotitle_standalone-web');
	const pageDescription = t(
		'lyricslandingpage_text_seodescription_standalone-web',
	);
	const {playlistAsRailItem} = useAsRailItems();
	const {data} = useLyricsPageData();
	return (
		<TempoLandingPage title={pageTitle} description={pageDescription}>
			<LandingSections topGutter>
				<LyricsHero />
				<LyricsClick />
				<LyricsTranslated />
				<LyricsAcross />
				<PlaylistSection
					title={t(
						'lyricslandingpage_title_karaokeplaylistssection_standalone-web',
					)}
					description={t(
						'lyricslandingpage_text_karaokeplaylistssection_standalone-web',
					)}
					playlistItems={playlistAsRailItem(data?.playlists.edges)}
				/>
				<LyricsShare />
				<LyricsSearch />
				<FAQ />
			</LandingSections>
		</TempoLandingPage>
	);
};

export const getStaticProps: GetStaticProps<LandingProps> = async ({
	locale = '',
	locales = [],
}) => {
	const translations = await serverSideTranslations(locale, [
		'lyrics',
		'features',
		'common',
	]);
	return {
		props: {
			...translations,
			dehydratedState: {
				...(await getFooterLinksPerLang({locale, locales})),
				...(await getLyricsPageGQLData({locale})),
			},
			tracking: {
				pagecategory: 'features',
				pagename: 'lyrics',
				cdpViewId: 'features_lyrics',
			},
		},
		revalidate: 15 * 60,
	};
};

export default LyricsPage;
